import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FluxButton, FluxCheckbox } from '@meltwater/flux-web-components-react';
import { Stack, Tooltip } from '@mui/material';
import styles from './UsersAndWorkspaces.module.css';
import CompanyServices from '../../services/company.services';
import { useCompanyId } from '../../services/CompanyidProvider';
import { exportWorkspacesConsolidated, trackExport } from '../../services/exportUtils';
import { useUserData } from './UsersAndWorkspaces';
import DataTable from '../../components/DataTable';

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

const GetWorkspaceData = ({ load, setActiveTab }) => {
    const { companyId } = useCompanyId();
    const { workspaceData, setWorkspaceData, setLoading } = useUserData();
    const [workspacesData, setWorkspacesData] = useState({ workspaces: [], isLoaded: false });
    const [openSnackBar, setOpenSnackBar] = useState(false);
    const [feedbackMsg, setFeedbackMsg] = useState('');
    const [severity, setSeverity] = useState('info');
    const [isLoading, setIsLoading] = useState(!workspaceData && !workspacesData.isLoaded);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [includeInternal, setIncludeInternal] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const loadingTimerRef = useRef(null);
    const apiAttemptedRef = useRef(false);
    const landingPageNavRef = useRef(false);
    
    // Custom guide steps specific to WorkspaceData
    const workspaceGuideSteps = useMemo(() => [
        {
            title: 'Workspace Data',
            text: 'This guide will help you navigate the Workspace Data features.',
            element: null,
        },
        {
            title: 'Search Workspaces',
            text: 'Use the search box to quickly find workspaces by name or ID.',
            element: '.mrt-input',
            position: { top: 20, left: 200 }
        },
        {
            title: 'Column Visibility & Table Settings',
            text: 'Use these buttons to show or hide specific columns and adjust table settings.',
            element: '.mrt-button.mrt-dropdown-button',
            position: { top: 0, right: 80 }
        },
        {
            title: 'Export Workspaces',
            text: 'You can export all workspaces or selected workspaces using these buttons.',
            element: '.MuiButtonBase-root.FluxButton-root',
            position: { top: 0, left: 100 }
        },
        {
            title: 'Export Information',
            text: 'Workspace exports include workspace details, users, searches, custom categories, filters, author lists, comparisons, monitors, document tags, monitored connections, and dashboards.',
            element: null,
        },
        {
            title: 'Include Internal Users',
            text: 'Toggle this checkbox to include internal Meltwater users in workspace exports.',
            element: '.MuiButtonBase-root.FluxCheckbox-root',
            position: { top: 0, left: 400 }
        },
        {
            title: 'Refresh Data',
            text: 'Use this button to refresh workspace data from the server.',
            element: 'button[title="Refresh Data"]',
            position: { top: 0, left: 300 }
        },
        {
            title: 'Workspace Selection',
            text: 'Select workspaces with checkboxes to perform operations on specific workspaces.',
            element: '.mrt-row-select-checkbox',
            position: { top: 100, left: 30 }
        },
        {
            title: 'Tooltip Information',
            text: 'Hover over numerical values to see details about contained items.',
            element: '[data-mui-internal-clone-element="true"]',
            position: { top: 100, left: 200 }
        }
    ], []);

    // Track initial load to identify direct navigation from landing page
    useEffect(() => {
        if (load && !landingPageNavRef.current) {
            console.log("Workspace tab activated first time from landing page");
            landingPageNavRef.current = true;
        }
    }, [load]);

    useEffect(() => {
        async function fetchData() {
            // Prevent API call if error occurred from landing page navigation
            if (hasError && landingPageNavRef.current && apiAttemptedRef.current) {
                return;
            }

            if (!workspaceData && !workspacesData.isLoaded && !apiAttemptedRef.current) {
                apiAttemptedRef.current = true; // Mark that we've attempted the API call
                
                try {
                    setIsLoading(true);
                    setLoading(true);
                    setLoadingMessage('Fetching workspace data...');
                    loadingTimerRef.current = setTimeout(() => {
                        setLoadingMessage('Please wait, it takes longer than usual for larger companies');
                    }, 10000);
                    
                    console.log("Initiating getWorkspaceData call for company:", companyId);
                    const response = await CompanyServices.getWorkspaceData(companyId);
                    console.log("getWorkspaceData call successful");
                    
                    setWorkspaceData(response.data);
                    setWorkspacesData({
                        workspaces: response.data.workspaces,
                        isLoaded: true
                    });
                    setFeedbackMsg(`Successfully loaded ${response.data.workspaces.length} workspaces.`);
                    setSeverity('success');
                    setOpenSnackBar(true);
                    setHasError(false);
                } catch (err) {
                    console.error("Error in getWorkspaceData API call:", err);
                    setOpenSnackBar(true);
                    const errorMsg = err.message || 'Unknown error';
                    setFeedbackMsg(`Error loading data: ${errorMsg}, please refresh or contact support.`);
                    setErrorMessage(errorMsg);
                    setSeverity('error');
                    setHasError(true);
                } finally {
                    if (loadingTimerRef.current) {
                        clearTimeout(loadingTimerRef.current);
                        loadingTimerRef.current = null;
                    }
                    setLoadingMessage('');
                    setIsLoading(false);
                    setLoading(false);
                }
            } else if (workspaceData && !workspacesData.isLoaded) {
                setWorkspacesData({
                    workspaces: workspaceData.workspaces,
                    isLoaded: true
                });
                setIsLoading(false);
            }
        }
        
        if (load) {
            fetchData();
        }
        return () => {
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
            }
        };
    }, [companyId, load, workspacesData.isLoaded, workspaceData, setWorkspaceData, setLoading, hasError]);

    const handleExport = async (selectedRows = null) => {
        try {
            setIsLoading(true);
            setLoadingMessage('Preparing workspace data export...');
            const workspacesToExport = selectedRows 
                ? selectedRows.map(row => row.original)
                : workspacesData.workspaces;
                
            // Pre-filter the workspace users based on includeInternal flag
            const filteredWorkspaces = workspacesToExport.map(workspace => {
                if (!includeInternal && workspace.users) {
                    // Create a new workspace object with filtered users
                    return {
                        ...workspace,
                        users: workspace.users.filter(user => !user.isInternal && !user.email?.includes('@meltwater.com'))
                    };
                }
                return workspace;
            });
                
            await trackExport(
                companyId, 
                filteredWorkspaces, 
                'workspace', 
                { 
                    type: selectedRows ? 'SelectedWorkspaces' : 'AllWorkspaces',
                    eventType: 'exportWorkspaceData',
                    includeInternal: includeInternal // parameter name remains the same in the component
                }
            );
            await exportWorkspacesConsolidated(filteredWorkspaces, companyId);
            setFeedbackMsg(`Successfully exported ${filteredWorkspaces.length} workspaces`);
            setSeverity('success');
        } catch (err) {
            console.error('Export error:', err);
            setFeedbackMsg('Export failed. Please try again or contact support.');
            setSeverity('error');
        } finally {
            setIsLoading(false);
            setOpenSnackBar(true);
        }
    };

    const handleCloseSnackBar = () => {
        setOpenSnackBar(false);
    };

    const refreshData = async () => {
        try {
            setIsLoading(true);
            apiAttemptedRef.current = false; // Reset API attempt for refresh
            setHasError(false); // Clear any previous errors
            setLoadingMessage('Refreshing workspace data...');
            loadingTimerRef.current = setTimeout(() => {
                setLoadingMessage('Please wait, it takes longer that usual for larger companies');
            }, 15000);
            
            console.log("Initiating getWorkspaceData refresh for company:", companyId);
            const response = await CompanyServices.getWorkspaceData(companyId, 'refresh');
            console.log("getWorkspaceData refresh successful");
            
            setWorkspacesData({
                workspaces: response.data.workspaces,
                isLoaded: true
            });

            setFeedbackMsg('Data refreshed successfully.');
            setSeverity('success');
            setOpenSnackBar(true);
        } catch (err) {
            console.error("Error refreshing workspace data:", err);
            setHasError(true); // Track error state
            const errorMsg = err.message || 'Unknown error';
            setErrorMessage(errorMsg);
            setFeedbackMsg(`Error refreshing data: ${errorMsg}, please try again or contact support.`);
            setSeverity('error');
            setOpenSnackBar(true);
        } finally {
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
                loadingTimerRef.current = null;
            }
            setIsLoading(false);
            setLoadingMessage('');
        }
    };

    const renderToolbarActions = ({ table }) => {
        const selectedRows = table.getSelectedRowModel().rows;
        const hasSelectedRows = selectedRows.length > 0;
        
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <FluxButton
                    tint="accent"
                    type="filled"
                    onClick={() => handleExport(selectedRows)}
                    disabled={!hasSelectedRows}
                >
                    Export Selected Workspaces
                </FluxButton>
                <FluxButton
                    tint="primary"
                    type="filled"
                    onClick={() => handleExport()}
                >
                    Export All Workspaces
                </FluxButton>
                <FluxButton
                    tint="secondary"
                    type="filled"
                    onClick={refreshData}
                >
                    Refresh Data
                </FluxButton>
                <FluxCheckbox
                    checked={includeInternal}
                    onFluxChange={(e) => setIncludeInternal(e.target.checked)}
                >
                    Include Internal Users
                </FluxCheckbox>
                <span>|</span>
                <span style={{ 
                    color: '#666',
                    fontSize: '0.9rem',
                    fontWeight: 500
                }}>
                    Total Workspaces: {workspacesData.workspaces.length}
                </span>
            </Stack>
        );
    };

    const columns = useMemo(
        () => [
            { 
                accessorKey: '_id', 
                header: 'ID',
                enableGlobalFilter: true 
            },
            { 
                accessorKey: 'name', 
                header: 'Workspace Name',
                enableGlobalFilter: true 
            },
            { 
                accessorKey: 'created', 
                header: 'Created Date',
                Cell: ({ cell }) => formatDate(cell.getValue())
            },
            { 
                accessorKey: 'userCount', 
                header: 'Users',
                Cell: ({ row }) => {
                    const totalCount = row.original.totalUserCount;
                    const users = row.original.users || [];
                    
                    if (!totalCount) return '0';
                    
                    const tooltipContent = users.map(u => `${u.email}${u.isInternal ? ' (Internal)' : ''}`).join('\n');
                    
                    return (
                        <Tooltip title={tooltipContent}>
                            <span>{totalCount}</span>
                        </Tooltip>
                    );
                }
            },
            { 
                accessorKey: 'searchCount', 
                header: 'Searches',
                Cell: ({ row }) => {
                    const count = row.original.searches?.length || 0;
                    if (count === 0) return '0';
                    return (
                        <Tooltip title={row.original.searches.map(s => s.name).join('\n')}>
                            <span>{count}</span>
                        </Tooltip>
                    );
                }
            },
            { 
                accessorKey: 'categoryCount', 
                header: 'Custom Categories',
                Cell: ({ row }) => {
                    const count = row.original.customCategories?.length || 0;
                    if (count === 0) return '0';
                    return (
                        <Tooltip title={row.original.customCategories.map(c => c.name).join('\n')}>
                            <span>{count}</span>
                        </Tooltip>
                    );
                }
            },
            { 
                accessorKey: 'filterCount', 
                header: 'Filter Sets',
                Cell: ({ row }) => {
                    const count = row.original.filters?.length || 0;
                    if (count === 0) return '0';
                    return (
                        <Tooltip title={row.original.filters.map(f => f.name).join('\n')}>
                            <span>{count}</span>
                        </Tooltip>
                    );
                }
            },
            {
                accessorKey: 'authorLists',
                header: 'Author Lists',
                Cell: ({ row }) => {
                    const authorLists = row.original.authorLists || [];
                    
                    if (authorLists.length === 0) return '0';
                    
                    const tooltipContent = authorLists.map(a => a.name).join('\n');
                    
                    return (
                        <Tooltip title={tooltipContent}>
                            <span>{authorLists.length}</span>
                        </Tooltip>
                    );
                }
            },
            {
                accessorKey: 'comparisons',
                header: 'Comparisons',
                Cell: ({ row }) => {
                    const comparisons = row.original.comparisons || [];
                    
                    if (comparisons.length === 0) return '0';
                    
                    const tooltipContent = comparisons.map(c => c.name).join('\n');
                    
                    return (
                        <Tooltip title={tooltipContent}>
                            <span>{comparisons.length}</span>
                        </Tooltip>
                    );
                }
            },
            {
                accessorKey: 'monitors',
                header: 'Monitors',
                Cell: ({ row }) => {
                    const monitors = row.original.monitors || [];
                    
                    if (monitors.length === 0) return '0';
                    
                    const tooltipContent = monitors.map(m => m.name).join('\n');
                    
                    return (
                        <Tooltip title={tooltipContent}>
                            <span>{monitors.length}</span>
                        </Tooltip>
                    );
                }
            },
            { 
                accessorKey: 'tagCount', 
                header: 'Tags',
                Cell: ({ row }) => {
                    const count = row.original.documentTags?.length || 0;
                    if (count === 0) return '0';
                    return (
                        <Tooltip title={row.original.documentTags.map(t => t.name).join('\n')}>
                            <span>{count}</span>
                        </Tooltip>
                    );
                }
            },
            {
                accessorKey: 'monitoredConnections',
                header: 'Monitored Connections',
                Cell: ({ row }) => {
                    const connections = row.original.monitoredConnections || { totalCount: 0 };
                    const totalCount = connections.totalCount || 0;
                    
                    if (totalCount === 0) return '0';
                    
                    const fbConnections = connections.facebook || [];
                    const igConnections = connections.instagram || [];
                    const liConnections = connections.linkedin || [];
                    
                    const tooltipItems = [
                        ...fbConnections.map(c => `Facebook: ${c.name}`),
                        ...igConnections.map(c => `Instagram: ${c.name}`),
                        ...liConnections.map(c => `LinkedIn: ${c.name}`)
                    ];
                    
                    return (
                        <Tooltip title={tooltipItems.join('\n')}>
                            <span>{totalCount}</span>
                        </Tooltip>
                    );
                }
            },
            { 
                accessorKey: 'dashboardCount', 
                header: 'Dashboards',
                Cell: ({ row }) => {
                    const customCount = row.original.dashboards?.custom?.length || 0;
                    const classicCount = row.original.dashboards?.classic?.length || 0;
                    const totalCount = customCount + classicCount;
                    
                    if (totalCount === 0) return '0';
                    
                    const tooltipContent = [
                        ...(row.original.dashboards?.custom?.map(d => `Custom: ${d.name}`) || []),
                        ...(row.original.dashboards?.classic?.map(d => `Classic: ${d.name}`) || [])
                    ].join('\n');
                    
                    return (
                        <Tooltip title={tooltipContent}>
                            <span>{totalCount}</span>
                        </Tooltip>
                    );
                }
            }
        ],
        []
    );

    return (
        <div className={styles.Search}>
            <DataTable 
                columns={columns}
                data={workspacesData.isLoaded ? workspacesData.workspaces : []}
                isLoading={isLoading}
                loadingMessage={loadingMessage || 'Loading workspace data...'}
                hasError={hasError && !workspacesData.isLoaded}
                errorMessage={errorMessage}
                errorTitle="Error Loading Workspace Data"
                onBackClick={() => setActiveTab(null)}
                renderToolbarActions={renderToolbarActions}
                backTooltip="Return to Dashboard"
                showGuide={false}
                guideSteps={workspaceGuideSteps}
                snackbarProps={{
                    open: openSnackBar,
                    onClose: handleCloseSnackBar,
                    message: feedbackMsg,
                    severity: severity,
                    autoHideDuration: 10000
                }}
                tableProps={{
                    muiSearchTextFieldProps: {
                        placeholder: 'Search workspaces...',
                        variant: 'standard',
                        sx: { width: '300px' }
                    },
                    initialState: { 
                        density: 'compact',
                        columnVisibility: {
                            categoryCount: false,
                            filterCount: false,
                            authorLists: false,
                            comparisons: false,
                            monitors: false,
                            monitoredConnections: false,
                        },
                        pagination: { pageSize: 15 }
                    },
                }}
            />
        </div>
    );
};

export default GetWorkspaceData;